const SERVICE_WORKER_URL = '/sw.js';
const SERVICE_WORKER_REGISTER_OPTIONS = { updateViaCache: 'none' } as const;
const APP_SHELL_CACHE = 'mlst-app-shell-v6';
const APP_SHELL_URLS = [
  '/',
  '/index.html',
  '/manifest.json',
  '/favicon.ico',
];

let serviceWorkerRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

function shouldUseServiceWorker(): boolean {
  return process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator;
}

function resolvePrecacheUrl(value: unknown): string | null {
  if (!value || typeof value !== 'string') {
    return null;
  }

  if (value.startsWith('http')) {
    return value;
  }

  return new URL(value, window.location.origin).toString();
}

function shouldPrecacheBuildAsset(value: unknown): value is string {
  return typeof value === 'string' && /\.(?:js|css)(?:$|\?)/.test(value);
}

function createPrecacheRequest(url: string): Request {
  const requestUrl = new URL(url, window.location.origin);
  return new Request(requestUrl.toString(), {
    mode: requestUrl.origin === window.location.origin ? 'same-origin' : 'no-cors',
    cache: 'no-store',
  });
}

async function getOfflineShellUrls(): Promise<string[]> {
  const urls = new Set(APP_SHELL_URLS.map((value) => resolvePrecacheUrl(value)).filter(Boolean) as string[]);

  try {
    const manifestResponse = await fetch('/asset-manifest.json', { cache: 'no-store' });
    if (!manifestResponse.ok) {
      return Array.from(urls);
    }

    const manifest = await manifestResponse.json();
    const manifestFiles = Object.values(manifest?.files || {});
    const manifestEntrypoints = Array.isArray(manifest?.entrypoints) ? manifest.entrypoints : [];

    manifestFiles.forEach((value: unknown) => {
      if (shouldPrecacheBuildAsset(value)) {
        const resolvedUrl = resolvePrecacheUrl(value);
        if (resolvedUrl) {
          urls.add(resolvedUrl);
        }
      }
    });

    manifestEntrypoints.forEach((value: unknown) => {
      if (shouldPrecacheBuildAsset(value)) {
        const resolvedUrl = resolvePrecacheUrl(value);
        if (resolvedUrl) {
          urls.add(resolvedUrl);
        }
      }
    });
  } catch (error) {
    console.warn('[SW] Failed to read asset manifest for page-side shell caching:', error);
  }

  return Array.from(urls);
}

async function precacheOfflineShellFromPage(): Promise<boolean> {
  if (typeof caches === 'undefined') {
    return false;
  }

  try {
    const [cache, urls] = await Promise.all([
      caches.open(APP_SHELL_CACHE),
      getOfflineShellUrls(),
    ]);

    await Promise.all(urls.map(async (url) => {
      try {
        const request = createPrecacheRequest(url);
        const response = await fetch(request);
        if (response && (response.ok || response.type === 'opaque')) {
          await cache.put(request, response.clone());
        }
      } catch (error) {
        console.warn('[SW] Failed to cache offline shell asset from page:', url, error);
      }
    }));

    return true;
  } catch (error) {
    console.warn('[SW] Failed to open page-side offline shell cache:', error);
    return false;
  }
}

function prepareRegistration(registration: ServiceWorkerRegistration): ServiceWorkerRegistration {
  const activateUpdate = () => {
    registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
  };

  registration.addEventListener('updatefound', () => {
    const installingWorker = registration.installing;
    if (!installingWorker) {
      return;
    }

    installingWorker.addEventListener('statechange', () => {
      if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
        activateUpdate();
      }
    });
  });

  if (registration.waiting) {
    activateUpdate();
  }

  registration.update().catch((error) => {
    console.warn('[SW] Update check failed:', error);
  });

  return registration;
}

async function registerOrGetServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!shouldUseServiceWorker()) {
    return null;
  }

  if (!serviceWorkerRegistrationPromise) {
    serviceWorkerRegistrationPromise = navigator.serviceWorker
      .register(SERVICE_WORKER_URL, SERVICE_WORKER_REGISTER_OPTIONS)
      .then((registration) => prepareRegistration(registration))
      .catch((error) => {
        serviceWorkerRegistrationPromise = null;
        console.warn('[SW] Registration failed:', error);
        return null;
      });
  }

  return serviceWorkerRegistrationPromise;
}

export async function ensureOfflineShellReady(
  timeoutMs = 10000
): Promise<{ ready: boolean; controlled: boolean; shellCached: boolean }> {
  const registration = await registerOrGetServiceWorker();
  if (!registration) {
    return { ready: false, controlled: false, shellCached: false };
  }

  if (registration.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }

  const ready = await new Promise<boolean>((resolve) => {
    const timeoutId = window.setTimeout(() => {
      resolve(false);
    }, timeoutMs);

    navigator.serviceWorker.ready
      .then(() => {
        window.clearTimeout(timeoutId);
        resolve(true);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        console.warn('[SW] Ready check failed:', error);
        resolve(false);
      });
  });

  const shellCached = ready ? await precacheOfflineShellFromPage() : false;

  return {
    ready,
    controlled: !!navigator.serviceWorker.controller,
    shellCached,
  };
}

export function registerServiceWorker(): void {
  if (!shouldUseServiceWorker()) {
    return;
  }

  void registerOrGetServiceWorker();
}
